import React, { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Picker from '../../components/nucleus/Picker';
import Segmented from '../../components/nucleus/Segmented';
import Switch from '../../components/nucleus/Switch';
import { Tags } from '../../components/nucleus/Inspector';
import { NodeMark } from '../../components/nucleus/faces';
import { searchNodes } from './data/api';

// A PLUGIN'S FIELDS, as its sheets ask for them: a credential (secret, never
// shown back), a setting (answered once) or an input (decided per run). One
// field is { key, label, description, type, options, secret, required, scope,
// is_set, value, default?, min?, max?, max_length?, schema? }, as GET
// /setup-config sends it (the last five where the server sends them). The
// types are the platform's (GET /plugins/field-types); an on/off one is the
// one Switch. Every input wears a real label, and its description, limits,
// default and error are read out with it (aria-describedby).

const NUMERIC = ['integer', 'float', 'number'];
const optionsOf = (f) => (f.options || []).map((o) => (typeof o === 'object'
    ? { value: o.value, label: o.label || String(o.value) } : { value: o, label: String(o) }));
const has = (v) => v !== undefined && v !== null && v !== '';

/** Nothing there: blank, or an empty list. */
export const blank = (v) => v === undefined || v === null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');

/** A value as the field's type wants it sent. */
export function typed(f, v) {
    if (f.type === 'boolean') return !!v;
    if (f.type === 'integer') return parseInt(v, 10);
    if (NUMERIC.includes(f.type)) return Number(v);
    if (f.type === 'multiselect') return Array.isArray(v) ? v : String(v).split(',').map((s) => s.trim()).filter(Boolean);
    return v;
}

/** A saved value, said for reading. */
export function shown(f, v) {
    if (blank(v)) return null;
    if (f.type === 'boolean') return v ? 'On' : 'Off';
    if (Array.isArray(v)) return v.join(', ');
    const opt = optionsOf(f).find((o) => String(o.value) === String(v));
    return opt ? opt.label : String(v);
}

/** The required fields with no value among `values` (falling back to what is saved), by label. */
export function missingOf(fields, values) {
    return fields.filter((f) => f.required && f.type !== 'boolean' && blank(values[f.key] ?? f.value)).map((f) => f.label || f.key);
}

// a field this sheet cannot take: a file is given by an app, not typed here
export const takesHere = (f) => f.type !== 'file';

/** The local time zone, as the browser names it ("Europe/London"). */
export function timeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

/** What a value breaks of its field's limits, in a few words, or null. */
export function problemOf(f, v) {
    if (blank(v) || f.type === 'boolean') return null;
    if (NUMERIC.includes(f.type)) {
        const n = Number(v);
        if (!Number.isFinite(n)) return 'Not a number';
        if (f.type === 'integer' && !Number.isInteger(n)) return 'A whole number';
        if (has(f.min) && n < Number(f.min)) return `At least ${f.min}`;
        if (has(f.max) && n > Number(f.max)) return `At most ${f.max}`;
        return null;
    }
    if (has(f.max_length) && typeof v === 'string' && v.length > Number(f.max_length)) return `At most ${f.max_length} characters`;
    return null;
}
/** Every field whose value breaks its limits: [{ key, label, problem }]. */
export function problemsOf(fields, values) {
    return (fields || [])
        .map((f) => ({ key: f.key, label: f.label || f.key, problem: problemOf(f, values[f.key] ?? f.value) }))
        .filter((x) => x.problem);
}

/** A field's limits and default, as one short line ("1 to 10 · Default 5"), or null. */
export function limitsLine(f) {
    const parts = [];
    if (NUMERIC.includes(f.type)) {
        if (has(f.min) && has(f.max)) parts.push(`${f.min} to ${f.max}`);
        else if (has(f.min)) parts.push(`At least ${f.min}`);
        else if (has(f.max)) parts.push(`At most ${f.max}`);
    } else if (has(f.max_length)) parts.push(`Up to ${f.max_length} characters`);
    if (has(f.default) && !f.secret) parts.push(`Default ${shown(f, f.default) ?? String(f.default)}`);
    if (f.type === 'datetime') { const tz = timeZone(); if (tz) parts.push(`Your time zone, ${tz}`); }
    return parts.length ? parts.join(' · ') : null;
}

// ── a node: picked from the subject's nodes of its schema ───────────────────

const titleOf = (n) => n?.value_json?.title || n?.title || 'Untitled';

/** A node field: a find over the space's nodes of the declared schema. When
 *  the nodes cannot be searched, a plain field for the node's id. */
export function NodeField({ id, f, value, onChange, disabled, invalid, describedBy, groupId = null }) {
    const [term, setTerm] = useState('');
    const [found, setFound] = useState(null);
    const [failed, setFailed] = useState(false);
    const [picking, setPicking] = useState(false);
    const [chosen, setChosen] = useState(null); // { id, title, schema }
    const schema = f.schema || null;
    // a schema narrows enough to list at once; any node wants two letters first
    const enough = schema ? true : term.trim().length >= 2;

    useEffect(() => {
        if (!picking || failed || !enough) { setFound(null); return undefined; }
        let live = true;
        const t = setTimeout(() => {
            searchNodes(groupId, { search: term.trim(), schema, limit: 8 })
                .then((list) => { if (live) setFound(list); })
                .catch(() => { if (live) setFailed(true); });
        }, 220);
        return () => { live = false; clearTimeout(t); };
    }, [term, picking, failed, enough, schema, groupId]);

    if (failed) {
        return (
            <>
                <input id={id} className="plugins-input is-mono" type="text" aria-invalid={invalid || undefined} aria-describedby={describedBy} disabled={disabled}
                    placeholder="A node’s id" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
                <span className="plugins-field__note">Your nodes could not be searched. Paste the node’s id.</span>
            </>
        );
    }
    if (!blank(value) && !picking) {
        const name = chosen && String(chosen.id) === String(value) ? chosen.title : String(value);
        return (
            <div className="plugins-node">
                <div className="plugins-node__chosen">
                    <NodeMark schema={chosen?.schema || schema} size={18} />
                    <span id={id} aria-describedby={describedBy}>{name}</span>
                    {!disabled && <button type="button" className="nx-link" onClick={() => setPicking(true)}>Change</button>}
                    {!disabled && <button type="button" className="nx-link" onClick={() => { onChange(''); setChosen(null); }}>Clear</button>}
                </div>
            </div>
        );
    }
    const take = (n) => {
        setChosen({ id: n.id, title: titleOf(n), schema: n.schema_name || schema });
        onChange(String(n.id));
        setPicking(false);
        setTerm('');
    };
    return (
        <div className="plugins-node">
            <input id={id} className="plugins-input" type="search" autoComplete="off" aria-invalid={invalid || undefined} aria-describedby={describedBy}
                disabled={disabled} placeholder={schema ? `Find a ${schema} by its title` : 'Find a node by its title'}
                value={term} onFocus={() => setPicking(true)} onChange={(e) => { setPicking(true); setTerm(e.target.value); }}
                onKeyDown={(e) => { if (e.key === 'Escape') setPicking(false); }} />
            {picking && enough && found && (
                <div className="plugins-node__list" role="listbox" aria-label={`${f.label || f.key}: nodes`}>
                    {found.length === 0 && <span className="plugins-node__opt" aria-disabled="true">No node by that title.</span>}
                    {found.map((n) => (
                        <button key={n.id} type="button" role="option" aria-selected={String(n.id) === String(value)} className="plugins-node__opt" onClick={() => take(n)}>
                            <NodeMark schemaId={n.schema_id} schema={n.schema_name} size={18} />
                            {titleOf(n)}
                            {n.schema_name && <small>{n.schema_name}</small>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── words typed as tags: a multiselect with no options ──────────────────────

export function TagsField({ id, value, onChange, disabled, invalid, describedBy }) {
    const [draft, setDraft] = useState('');
    const list = Array.isArray(value) ? value : blank(value) ? [] : String(value).split(',').map((s) => s.trim()).filter(Boolean);
    const add = (word) => {
        const w = word.trim();
        if (w && !list.includes(w)) onChange([...list, w]);
        setDraft('');
    };
    return (
        <div className="plugins-tags">
            {list.map((w) => (
                <span key={w} className="plugins-tags__tag">
                    {w}
                    {!disabled && (
                        <button type="button" className="plugins-tags__x" aria-label={`Remove ${w}`} onClick={() => onChange(list.filter((x) => x !== w))}>
                            <X size={11} strokeWidth={2} aria-hidden="true" />
                        </button>
                    )}
                </span>
            ))}
            <input id={id} className="plugins-tags__in" type="text" disabled={disabled} aria-invalid={invalid || undefined} aria-describedby={describedBy}
                placeholder={list.length ? '' : 'Type, then Enter'} value={draft}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v.includes(',')) {
                        const parts = v.split(',');
                        const words = parts.slice(0, -1).map((x) => x.trim()).filter((x) => x && !list.includes(x));
                        if (words.length) onChange([...list, ...new Set(words)]);
                        setDraft(parts[parts.length - 1]);
                    } else setDraft(v);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); add(draft); }
                    if (e.key === 'Backspace' && !draft && list.length) onChange(list.slice(0, -1));
                }}
                onBlur={() => { if (draft.trim()) add(draft); }} />
        </div>
    );
}

// ── one input, by type ───────────────────────────────────────────────────────

function Input({ id, f, value, onChange, disabled, invalid, describedBy, groupId }) {
    const label = f.label || f.key;
    const common = { id, disabled, 'aria-invalid': invalid || undefined, 'aria-describedby': describedBy };
    if (f.secret) {
        return (
            <input {...common} className="plugins-input" type="password" autoComplete="off"
                placeholder={f.is_set ? '••••••••' : ''} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
        );
    }
    if (f.type === 'boolean') {
        return <Switch id={id} aria-describedby={describedBy} checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
    }
    if (f.type === 'select') {
        return <Picker id={id} fill label={label} value={value ?? ''} disabled={disabled} onChange={(v) => onChange(v)} options={[{ value: '', label: '—' }, ...optionsOf(f)]} />;
    }
    if (f.type === 'multiselect') {
        if ((f.options || []).length) {
            const opts = optionsOf(f).map((o) => ({ id: o.value, label: o.label }));
            return <Segmented multiple label={label} options={opts} value={Array.isArray(value) ? value : []} onChange={onChange} />;
        }
        return <TagsField id={id} value={value} onChange={onChange} disabled={disabled} invalid={invalid} describedBy={describedBy} />;
    }
    if (f.type === 'node') {
        return <NodeField id={id} f={f} value={value} onChange={onChange} disabled={disabled} invalid={invalid} describedBy={describedBy} groupId={groupId} />;
    }
    if (f.type === 'textarea') {
        return <textarea {...common} className="plugins-input is-area" maxLength={has(f.max_length) ? Number(f.max_length) : undefined}
            placeholder={has(f.default) ? String(f.default) : undefined} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    }
    if (f.type === 'file') return null;
    const numeric = NUMERIC.includes(f.type);
    const type = numeric ? 'number' : f.type === 'date' ? 'date' : f.type === 'datetime' ? 'datetime-local' : 'text';
    return (
        <input {...common} className="plugins-input" type={type}
            min={numeric && has(f.min) ? f.min : undefined} max={numeric && has(f.max) ? f.max : undefined}
            step={f.type === 'integer' ? 1 : numeric ? 'any' : undefined}
            maxLength={!numeric && has(f.max_length) ? Number(f.max_length) : undefined}
            placeholder={has(f.default) ? String(f.default) : undefined}
            value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    );
}

// a group of choices (Segmented) names itself by the field's label; its heading is no <label>
const grouped = (f) => f.type === 'multiselect' && (f.options || []).length > 0;

/** One field in a sheet: its name (and whether it is needed or set), what it
 *  is for, its limits and default, its input, and what is wrong with it.
 *  `missing`: the server said this run needs it — marked, and focused.
 *  `error`: a reason of the sheet's own; else a broken limit is said.
 *  `groupId`: the space a node field searches. */
export function Field({ field: f, value, onChange, disabled = false, missing = false, error = null, groupId = null }) {
    const ref = useRef(null);
    const uid = useId();
    const id = `plugins-field-${uid}`;
    useEffect(() => {
        if (missing) ref.current?.querySelector('input, textarea, button')?.focus();
    }, [missing]);
    const label = f.label || f.key;
    const limits = limitsLine(f);
    const problem = error || (missing ? null : problemOf(f, value));
    const fromApp = f.type === 'file';
    const group = grouped(f);
    const ids = [
        f.description && `${id}-about`,
        limits && `${id}-limits`,
        fromApp && `${id}-app`,
        problem && `${id}-error`,
    ].filter(Boolean).join(' ') || undefined;
    const Name = group || fromApp ? 'span' : 'label';
    return (
        <div ref={ref} className={`plugins-field${missing || problem ? ' is-missing' : ''}`} data-missing={missing || undefined}>
            <span className="plugins-field__head">
                <Name id={`${id}-name`} className="plugins-label" {...(Name === 'label' ? { htmlFor: id } : {})}>{label}</Name>
                <Tags items={[
                    missing && { label: 'needs a value', tone: 'live' },
                    !missing && f.required && { label: 'required' },
                    f.secret && f.is_set && { label: 'set · replace' },
                    fromApp && { label: 'from an app', tone: 'warn' },
                ]} />
            </span>
            {f.description && <span id={`${id}-about`} className="plugins-field__about">{f.description}</span>}
            {fromApp
                ? <span id={`${id}-app`} className="plugins-field__note">An app gives this file when it runs the tool. It can’t be set here.</span>
                : <Input id={id} f={f} value={value} onChange={onChange} disabled={disabled} invalid={missing || !!problem} describedBy={ids} groupId={groupId} />}
            {limits && <span id={`${id}-limits`} className="plugins-field__note">{limits}</span>}
            {problem && <p id={`${id}-error`} className="plugins-field__error">{problem}</p>}
        </div>
    );
}
